import { useEffect, useRef, useState } from 'react';

import { dayjs, Ability, FORMAT_DATE_API } from '@umanforge/common';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { WarningIcon } from 'components/icons/warning_icon.component';

interface ExpirationAbilityProps {
  ability: Ability;
  onChange: ( ability: { id: string, expirationDate: string } ) => void;
}


export function ExpirationAbility( { ability, onChange }: ExpirationAbilityProps ) {
  const { t } = useTranslation();
  const input = useRef( null );

  const [ expirtationDate, setExpirtationDate ] = useState( ability.expirationDate ? dayjs( ability.expirationDate ) : dayjs().add( 1, 'days' ).startOf( 'day' ) );
  const [ isExpired, setIsExpired ] = useState( ( ability.expirationDate ? dayjs( ability.expirationDate ) : dayjs().add( 1, 'days' ).startOf( 'day' ) ).isBefore( dayjs(), 'date' ) );

  useEffect( () => {
    setIsExpired( expirtationDate.isBefore( dayjs(), 'date' ) );
  }, [ expirtationDate ] );

  return (
    <div className='w-full font-semibold text-[12px]'>
      <div className={ 'flex ' + ( isExpired ? 'rounded-md bg-red-400 -my-[2px]' : '' ) }>
        <input
          id={ 'expirationDate-' + ability.id }
          type="date"
          className={
            'w-full pl-1 py-1 rounded-md cursor-pointer select-none ' +
            ( isExpired ? 'border-[2px] border-red-400' : '' )
          }
          required
          ref={ input }
          onClick={ () => {
            if ( input.current ) {
              ( input.current as HTMLInputElement ).showPicker();
            }
          } }
          value={ expirtationDate.format( FORMAT_DATE_API ) }
          onChange={ e => {
            const newDate = dayjs( e.target.value, FORMAT_DATE_API );
            const oldDate = dayjs( e.target.defaultValue, FORMAT_DATE_API );
            const difference = newDate.diff( oldDate, 'month', true );
            setExpirtationDate( newDate );
            if ( !Number.isInteger( difference ) || difference == 0 ) {
              onChange( { id: ability.id, expirationDate: newDate.format( FORMAT_DATE_API ) } );
            }
          } }
          onBlur={ e => {
            const newDate = dayjs( e.target.value, FORMAT_DATE_API );
            if ( !newDate.isSame( dayjs( ability.expirationDate ) ) ) {
              setExpirtationDate( newDate );
              onChange( { id: ability.id, expirationDate: newDate.format( FORMAT_DATE_API ) } );
            }
          } }
        />
        {
          isExpired ?
            <>
              <div
                className='flex items-center justify-center w-8 h-8 px-1'
                id={ 'expiredAbility-' + ability.id }
              >
                <WarningIcon className='w-full h-full fill-white'/>
              </div>
              <Tooltip
                anchorSelect={ '#expiredAbility-' + ability.id }
                className='z-[1000] max-w-[600px] text-wrap'
                place="top"
                positionStrategy="fixed"
              >
                <span>
                  <span className='text-[13px] font-semibold'> { t( 'Authorization expired' ) } </span>
                </span>
              </Tooltip>
            </>
            :
            null
        }
      </div>
      <Tooltip
        anchorSelect={ '#expirationDate-' + ability.id }
        className='z-[1000] max-w-[600px] text-wrap'
        place="top"
        positionStrategy="fixed"
      >
        <span className='flex gap-1 items-center'>
          <span className='text-[13px] font-semibold'> { t( 'Expiry date' ) } </span>
          <p> { t( '(date_included)' ) } </p>
        </span>
      </Tooltip>
    </div>
  );
}
